import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../context/AuthContext'

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt     = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
const fmtTime = (d) => d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'
const money   = (n) => n != null ? `${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} RWF` : '—'
const moneyN  = (n) => Number(n || 0).toFixed(2)

// Fee breakdown — ipayPct and platformPct come from app_config (ipay_fee_pct, platform_commission_pct).
// vatPct is a decimal multiplier (e.g. 0.18 for 18%) when VAT is enabled, otherwise 0.
const breakdown = (gross, deliveryFee = 0, ipayPct = 0, platformPct = 0, vatPct = 0) => {
  const g        = Number(gross || 0)
  const df       = Number(deliveryFee || 0)
  const vat      = vatPct > 0 ? +(g * vatPct).toFixed(2) : 0
  const ipay     = +(g * ipayPct).toFixed(2)
  const platform = +(g * platformPct).toFixed(2)
  const payout   = +(g - ipay - platform).toFixed(2)
  return { gross: g, full_charge: g + df, delivery_fee: df, vat_fee: vat, ipay_fee: ipay, platform_fee: platform, seller_payout: payout }
}

const timeAgo = (d) => {
  const mins = Math.floor((Date.now() - new Date(d)) / 60000)
  if (mins < 60)  return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24)   return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

const avatarBg = (str = '') => {
  const c = ['#fde8d8','#dff5e8','#e8f4ff','#fef3d0','#ede8ff','#fce7f3']
  let h = 0; for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h)
  return c[Math.abs(h) % c.length]
}
const initials = (n = '') => (n.trim().charAt(0) || '?').toUpperCase()

// ── CSV Export ────────────────────────────────────────────────────────────────
function exportCSV(rows, period, feeConfig = {}) {
  const iPct   = Math.round((feeConfig.ipay_pct     ?? 0) * 100)
  const pPct   = Math.round((feeConfig.platform_pct ?? 0) * 100)
  const hasVat = rows.some(r => Number(r.vat_amount) > 0)
  const VERIF = { verified: 'Verified', pending: 'Pending Review', rejected: 'REJECTED', unverified: 'UNVERIFIED' }
  const headers = [
    'Order ID', 'Date', 'Payment Ref', 'Buyer', 'Buyer Phone',
    'Shop', 'Verification Status', 'Payment Method',
    'Gross (RWF)', 'Delivery Fee (RWF)',
    ...(hasVat ? ['VAT (RWF)'] : []),
    `iPay Fee ${iPct}% (RWF)`, `Platform Fee ${pPct}% (RWF)`, 'Seller Payout (RWF)',
    'Driver Name', 'Driver Phone', 'Delivery Status',
    'Payment Status',
  ]

  const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`

  const lines = rows.map(r => {
    const gross = Number(r.amount || r.total_amount || 0)
    const b     = breakdown(gross, r.delivery_fee, feeConfig.ipay_pct ?? 0, feeConfig.platform_pct ?? 0)
    const ipay  = r.ipay_fee     != null ? Number(r.ipay_fee)     : b.ipay_fee
    const plat  = r.platform_fee != null ? Number(r.platform_fee) : b.platform_fee
    const pay   = r.seller_payout != null ? Number(r.seller_payout) : b.seller_payout
    return [
      r.order_id,
      fmtTime(r.paid_at || r.created_at),
      r.payment_ref || r.transaction_id || '',
      r.buyer_name  || '',
      r.buyer_phone || '',
      r.shop_name   || '',
      VERIF[r.verification_status] || (r.verification_status ?? 'UNVERIFIED'),
      r.payment_method || '',
      moneyN(gross),
      moneyN(r.delivery_fee || 0),
      ...(hasVat ? [moneyN(r.vat_amount || 0)] : []),
      moneyN(ipay),
      moneyN(plat),
      moneyN(pay),
      r.driver_name   || '',
      r.driver_phone  || '',
      r.delivery_status || '',
      r.payment_status || '',
    ].map(escape).join(',')
  })

  const csv  = [headers.join(','), ...lines].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href     = url
  a.download = `kudos-payout-report-${period}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

// ── Refunds CSV Export ───────────────────────────────────────────────────────
function exportRefundsCSV(rows, period) {
  const headers = ['Refund ID','Order ID','Requested','Buyer','Shop','Reason','Amount (RWF)','Method','Status','Refund Ref','Refunded At']
  const escape  = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`
  const lines   = rows.map(r => [
    r.refund_id, r.order_id,
    fmtTime(r.requested_at),
    r.buyer_name || '', r.shop_name || '',
    r.reason_label || r.reason || '',
    Number(r.refund_amount || 0).toFixed(2),
    r.payment_method || '',
    r.refund_status || '',
    r.refund_ref || '',
    r.refunded_at ? fmtTime(r.refunded_at) : '',
  ].map(escape).join(','))
    const csv  = [headers.join(','), ...lines].join('\n')

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a'); a.href = url
  a.download = `kudos-refunds-${period}.csv`; a.click()
  URL.revokeObjectURL(url)
}

// ── Shop Report CSV Export ────────────────────────────────────────────────────
function exportShopReportCSV({ shopName, txns, tips, summary, dateFrom, dateTo, feeConfig = {}, tierInfo = null }) {
  const escape  = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`
  const period  = `${dateFrom}_${dateTo}`
  const iPct    = Math.round((feeConfig.ipay_pct     ?? 0) * 100)
  const pPct    = Math.round((feeConfig.platform_pct ?? 0) * 100)
  const effPPct = tierInfo?.platform_pct_effective != null
    ? Math.round(tierInfo.platform_pct_effective * 100)
    : pPct
  const hasVat  = txns.some(t => Number(t.vat_amount) > 0)

  // Transactions section
  const txnHeaders = ['Order ID','Date','Buyer','Buyer Phone','Method','Zone / Fallback','Delivery Fee (RWF)','Gross (RWF)',...(hasVat ? ['VAT (RWF)'] : []),`iPay ${iPct}% (RWF)`,`Platform ${effPPct}% (RWF)`,'Seller Payout (RWF)']
  const shopPlatformPct = tierInfo?.platform_pct_effective ?? feeConfig.platform_pct ?? 0
  const txnLines = txns.map(t => {
    const b = breakdown(t.shop_subtotal, t.delivery_fee, feeConfig.ipay_pct ?? 0, shopPlatformPct)
    const zoneLabel = t.zone_name ? `Zone: ${t.zone_name}${t.zone_city ? ' · ' + t.zone_city : ''}` : t.delivery_fee > 0 ? 'Fallback' : 'Free'
    return [
      t.order_id,
      fmtTime(t.paid_at),
      t.buyer_name  || '',
      t.buyer_phone || '',
      t.payment_method || '',
      zoneLabel,
      moneyN(b.delivery_fee),
      moneyN(b.gross),
      ...(hasVat ? [moneyN(t.vat_amount || 0)] : []),
      moneyN(b.ipay_fee),
      moneyN(b.platform_fee),
      moneyN(b.seller_payout),
    ].map(escape).join(',')
  })

  // Tips section — gross → iPay fee → platform (tip commission) fee → net,
  // all sourced from the backend's locked-in platform_fee/net_amount columns
  const tipHeaders = ['Tip ID','Date','Buyer','Buyer Phone','Emoji','Gross Tip (RWF)','iPay Fee (RWF)','Platform Fee (RWF)','Net Tip Payout (RWF)','Method']
  const tipLines = tips.map(t => {
    const gross    = Number(t.gross_amount || t.amount || 0)
    const ipayFee  = Number(t.ipay_fee     || 0)
    const platFee  = Number(t.platform_fee || 0)
    const net      = Number(t.net_amount   || gross - ipayFee - platFee)
    return [
      t.tip_id,
      fmtTime(t.created_at),
      t.buyer_name  || '',
      t.buyer_phone || '',
      t.emoji       || '💰',
      moneyN(gross), moneyN(ipayFee), moneyN(platFee), moneyN(net),
      t.payment_method || '',
    ].map(escape).join(',')
  })

  // Summary section
  const summaryLines = [
    ['Shop', shopName],
    ['Period', `${dateFrom} to ${dateTo}`],
    ['Orders', summary.order_count],
    ...(tierInfo?.tier ? [
      ['Tier', tierInfo.tier],
      ['Platform Fee % (Base)',      (tierInfo.platform_pct_base      * 100).toFixed(0) + '%'],
      ['Platform Fee % (Effective)', (tierInfo.platform_pct_effective * 100).toFixed(0) + '%'],
      ['Tier Discount (RWF)',        moneyN(Number(summary.gross || 0) * (tierInfo.tier_discount || 0))],
    ] : []),
    ['Gross Collected (RWF)', moneyN(summary.gross)],
    ['Delivery Total (RWF)',  moneyN(summary.delivery_total)],
    ...(Number(summary.vat_total) > 0 ? [['VAT (RWF)', moneyN(summary.vat_total)]] : []),
    [`iPay Fee ${iPct}% (RWF)`,          moneyN(summary.ipay_total)],
    [`Platform Fee ${effPPct}% (RWF)`,   moneyN(summary.platform_total)],
    ['Seller Payout (RWF)',   moneyN(summary.seller_total)],
    ['Tips Gross (RWF)',        moneyN(summary.tips_gross_total    || summary.tips_total || 0)],
    ['Tips iPay Fee (RWF)',     moneyN(summary.tips_ipay_total     || 0)],
    ['Tips Platform Fee (RWF)', moneyN(summary.tips_platform_total || 0)],
    ['Tips Net Payout (RWF)',   moneyN(summary.tips_total          || 0)],
    ['FINAL PAYOUT (RWF)',    moneyN(summary.final_payout)],
  ].map(([k, v]) => [escape(k), escape(v)].join(','))

  const csv = [
    escape('=== SUMMARY ==='), '',
    ...summaryLines, '',
    escape('=== TRANSACTIONS ==='), '',
    txnHeaders.join(','),
    ...txnLines, '',
    ...(tipLines.length > 0 ? [escape('=== TIPS ==='), '', tipHeaders.join(','), ...tipLines] : []),
  ].join('\n')

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a'); a.href = url
  a.download = `kudos-shop-report-${shopName.replace(/\s+/g,'-')}-${period}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

// ── Tips CSV Export ───────────────────────────────────────────────────────────
function exportTipsCSV(rows, period) {
  const headers = ['Tip ID','Date','Buyer','Shop','Seller','Emoji','Gross (RWF)','iPay Fee (RWF)','Platform Fee (RWF)','Net Payout (RWF)','Method','Stream Channel']
  const escape  = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`
  const lines   = rows.map(r => {
    const gross   = Number(r.amount || 0)
    const ipayFee = Number(r.ipay_fee     || 0)
    const platFee = Number(r.platform_fee || 0)
    const net     = Number(r.net_amount   || gross - ipayFee - platFee)
    return [
      r.tip_id,
      fmtTime(r.created_at),
      r.buyer_name  || '',
      r.shop_name   || '',
      r.seller_name || '',
      r.emoji       || '💰',
      moneyN(gross), moneyN(ipayFee), moneyN(platFee), moneyN(net),
      r.payment_method || '',
      r.stream_channel || '',
    ].map(escape).join(',')
  })
  const csv  = [headers.join(','), ...lines].join('\n')

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a'); a.href = url
  a.download = `kudos-tips-${period}.csv`; a.click()
  URL.revokeObjectURL(url)
}

// ── Tier metadata ─────────────────────────────────────────────────────────────
const TIER_META = {
  bronze:   { label: 'Bronze',   icon: '🥉', color: '#92400e', bg: '#fef3c7', border: '#fde68a' },
  silver:   { label: 'Silver',   icon: '🥈', color: '#374151', bg: '#f3f4f6', border: '#d1d5db' },
  gold:     { label: 'Gold',     icon: '🥇', color: '#b45309', bg: '#fffbeb', border: '#fcd34d' },
  platinum: { label: 'Platinum', icon: '💎', color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe' },
}

function TierBadge({ tier, small }) {
  const m = TIER_META[tier] || TIER_META.bronze
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3,
      background: m.bg, color: m.color, border: `1px solid ${m.border}`,
      fontSize: small ? 8 : 10, fontWeight: 700, padding: small ? '1px 5px' : '2px 7px',
      borderRadius: 4, fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap' }}>
      {m.icon} {m.label}
    </span>
  )
}

// ── Sub-components ─────────────────────────────────────────────────────────────
function SummaryCard({ label, value, sub, accent, bold }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '16px 18px', flex: 1 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: bold ? 22 : 20, fontWeight: 800, letterSpacing: '-0.5px', color: accent || '#111317' }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: '#9a9a94', marginTop: 4, fontFamily: "'JetBrains Mono',monospace" }}>{sub}</div>}
    </div>
  )
}

function FeeRow({ label, value, accent, borderTop, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderTop: borderTop ? '2px solid #e9e9e4' : '1px solid #f3f3f0' }}>
      <span style={{ fontSize: 13, color: bold ? '#111317' : '#6b7280', fontWeight: bold ? 700 : 500 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: bold ? 800 : 600, fontFamily: "'JetBrains Mono',monospace", color: accent || (bold ? '#111317' : '#374151') }}>{value}</span>
    </div>
  )
}

// ── Transaction Row Drawer ────────────────────────────────────────────────────
function TxnDrawer({ txn, onClose, feeConfig = {} }) {
  if (!txn) return null
  const iPct      = +((feeConfig.ipay_pct     ?? 0) * 100).toFixed(2).replace(/\.?0+$/, '')
  const pPct      = +((feeConfig.platform_pct ?? 0) * 100).toFixed(2).replace(/\.?0+$/, '')
  const gross     = Number(txn.amount || txn.total_amount || 0)
  const vatAmt    = Number(txn.vat_amount   || 0)
  const subtotal  = vatAmt > 0 ? gross - vatAmt : gross
  const delivFee  = Number(txn.delivery_fee || 0)
  // Use backend-computed values (already VAT-aware). Fall back to local calc for mock data.
  const ipayFee   = txn.ipay_fee    != null ? Number(txn.ipay_fee)    : +(subtotal * (feeConfig.ipay_pct     ?? 0)).toFixed(2)
  const platFee   = txn.platform_fee != null ? Number(txn.platform_fee) : +(subtotal * (feeConfig.platform_pct ?? 0)).toFixed(2)
  const sellerPay = txn.seller_payout != null ? Number(txn.seller_payout) : +(subtotal - ipayFee - platFee).toFixed(2)
  const bg        = avatarBg(txn.buyer_name || '')

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.18)', zIndex: 200 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 440, background: '#fff', zIndex: 201, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 40px rgba(0,0,0,0.12)', animation: 'slideIn 0.22s ease' }}>
        <style>{`@keyframes slideIn{from{transform:translateX(40px);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>

        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 42, height: 42, borderRadius: 9, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontWeight: 800, flexShrink: 0 }}>{initials(txn.buyer_name || txn.buyer_phone)}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800 }}>{txn.buyer_name || 'Anonymous'}</div>
            <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>Order #{txn.order_id} · {fmtTime(txn.paid_at || txn.created_at)}</div>
          </div>
          <div onClick={onClose} style={{ cursor: 'pointer', fontSize: 20, color: '#9a9a94' }}>×</div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 22 }}>

          {/* Fee breakdown card */}
          <div style={{ background: '#f9f9f7', borderRadius: 10, padding: '16px 18px', marginBottom: 20, border: '1px solid #e9e9e4' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 4 }}>Fee Breakdown</div>
            <FeeRow label="Gross Amount"                    value={money(gross)}                               />
            {vatAmt > 0 && <FeeRow label="— VAT"           value={`- ${money(vatAmt)}`}        accent="#0369a1" />}
            {vatAmt > 0 && <FeeRow label="Subtotal (ex-VAT)" value={money(subtotal)}            accent="#374151" />}
            <FeeRow label={`— iPay Fee (${iPct}%)`}        value={`- ${money(ipayFee)}`}        accent="#b91c1c" />
            <FeeRow label={`— Platform Fee (${pPct}%)`}    value={`- ${money(platFee)}`}        accent="#7c3aed" />
            <FeeRow label="Seller Payout"                  value={money(sellerPay)}             accent="#15803d" bold borderTop />
            {delivFee > 0 && <FeeRow label="Delivery Fee (→ driver)" value={money(delivFee)}   accent="#b45309" />}
          </div>

          {/* Payment info */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 8 }}>Payment</div>
            {[
              ['Ref',        txn.payment_ref || txn.transaction_id || '—'],
              ['Method',     (txn.payment_method || '—').toUpperCase()],
              ['Number',     txn.payment_number || '—'],
              ['Status',     txn.payment_status || '—'],
              ['Shop',       txn.shop_name || '—'],
              ['Buyer Phone',txn.buyer_phone || '—'],
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f3f0' }}>
                <span style={{ fontSize: 12, color: '#9a9a94', fontWeight: 600 }}>{k}</span>
                <span style={{ fontSize: 12, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#111317', wordBreak: 'break-all', textAlign: 'right', maxWidth: 240 }}>{v}</span>
              </div>
            ))}
          </div>

          {/* Driver info */}
          {(txn.driver_name || txn.driver_phone) && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 8 }}>Driver / Delivery</div>
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 7, background: avatarBg(txn.driver_name || ''), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800 }}>
                    {initials(txn.driver_name || txn.driver_phone)}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{txn.driver_name || 'Unknown Driver'}</div>
                    <div style={{ fontSize: 11, color: '#15803d', fontFamily: "'JetBrains Mono',monospace" }}>{txn.driver_phone || '—'}</div>
                  </div>
                </div>
                {[
                  ['Delivery Fee',    money(delivFee)],
                  ['Company',         txn.driver_company || 'Unaffiliated'],
                  ['Vehicle',         txn.vehicle_type || '—'],
                  ['Delivery Status', txn.delivery_status || '—'],
                  ['Delivered At',    txn.delivered_at ? fmtTime(txn.delivered_at) : '—'],
                ].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                    <span style={{ fontSize: 11, color: '#15803d', fontWeight: 600 }}>{k}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#111317' }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No driver */}
          {!txn.driver_name && !txn.driver_phone && (
            <div style={{ background: '#f9f9f7', border: '1px solid #e9e9e4', borderRadius: 8, padding: '12px 14px', marginBottom: 20, fontSize: 12, color: '#9a9a94' }}>
              No driver assigned to this order.
            </div>
          )}
        </div>
      </div>
    </>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function PayoutReport() {
  // Default: current month
  const now       = new Date()
  const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
  const monthEnd   = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    .toISOString().slice(0, 10)

  const [feeConfig, setFeeConfig] = useState(null)

  const [transactions, setTransactions] = useState([])
  const [loading,      setLoading]      = useState(true)
  const [error,        setError]        = useState('')
  const [dateFrom,     setDateFrom]     = useState(monthStart)
  const [dateTo,       setDateTo]       = useState(monthEnd)
  const [searchInput,  setSearchInput]  = useState('')
  const [search,       setSearch]       = useState('')
  const [methodFilter, setMethodFilter] = useState('')
  const [page,         setPage]         = useState(1)
  const [totalPages,   setTotalPages]   = useState(1)
  const [totalCount,   setTotalCount]   = useState(0)
  const [summary,      setSummary]      = useState({
    gross: 0, vat_total: 0, delivery_total: 0, driver_delivery_total: 0, seller_delivery_gross: 0, seller_delivery_net: 0, seller_delivery_total: 0, ipay_total: 0, platform_total: 0, seller_total: 0, count: 0,
  })
  const [selected,     setSelected]     = useState(null)
  const [exporting,    setExporting]    = useState(false)
  const [refunds,      setRefunds]      = useState([])
  const [refundSummary,setRefundSummary]= useState({ total_count:0, refunded_count:0, refunded_amount:0, pending_count:0, pending_amount:0 })
  const [refundTab,    setRefundTab]    = useState('transactions')  // 'transactions' | 'refunds' | 'tips' | 'shop'
  const [tips,         setTips]         = useState([])
  const [tipSummary,   setTipSummary]   = useState({ total_tips:0, total_amount:0, avg_tip:0, biggest_tip:0, unique_buyers:0, week_amount:0, month_amount:0 })
  const [pdfExporting, setPdfExporting] = useState(false)
  const allRowsRef = useRef([])
  const fetchIdRef  = useRef(0)

  // ── Shop Report state ──────────────────────────────────────────────────────
  const [shopQuery,       setShopQuery]       = useState('')          // search input
  const [shopSuggestions, setShopSuggestions] = useState([])          // dropdown results
  const [shopSugLoading,  setShopSugLoading]  = useState(false)
  const [selectedShop,    setSelectedShop]    = useState(null)        // { shop_id, shop_name }
  const [shopTxns,        setShopTxns]        = useState([])
  const [shopTips,        setShopTips]        = useState([])
  const [shopLoading,     setShopLoading]     = useState(false)
  const [shopExporting,   setShopExporting]   = useState(false)
  const [shopPage,        setShopPage]        = useState(1)
  const [shopTotalPages,  setShopTotalPages]  = useState(1)
  const [shopTotalCount,  setShopTotalCount]  = useState(0)
  const [shopSummary,     setShopSummary]     = useState({ gross:0, vat_total:0, delivery_total:0, driver_delivery_total:0, seller_delivery_gross:0, seller_delivery_commission:0, seller_delivery_payout:0, ipay_total:0, platform_total:0, seller_total:0, tips_total:0, tips_count:0, final_payout:0, order_count:0, driver_comm_pct:0 })
  const [shopTier,        setShopTier]        = useState(null)
  const shopFetchIdRef = useRef(0)

  // ── Pending Payments state ──────────────────────────────────────────────────
  const [pendingPayments,      setPendingPayments]      = useState([])
  const [pendingLoading,       setPendingLoading]       = useState(false)
  const [pendingPage,          setPendingPage]          = useState(1)
  const [pendingTotalPages,    setPendingTotalPages]    = useState(1)
  const [pendingTotalCount,    setPendingTotalCount]    = useState(0)
  const [pendingSummary,       setPendingSummary]       = useState({ count: 0, total_amount: 0 })
  const [pendingExpiryMinutes, setPendingExpiryMinutes] = useState(30)
  const pendingFetchIdRef = useRef(0)

  const PER_PAGE = 25

  // Fetch live fee percentages from app_config once on mount
  useEffect(() => {
    api.get('/settings/admin/config')
      .then(res => {
        const cfg = res.data.config || {}
        setFeeConfig({
          ipay_pct:     Number(cfg.ipay_fee_pct)            || 0,
          platform_pct: Number(cfg.platform_commission_pct) || 0,
          vat_pct:      Number(cfg.vat_pct)                 || 0,
          vat_enabled:  cfg.vat_enabled === true || cfg.vat_enabled === 'true',
        })
      })
      .catch(() => setFeeConfig({ ipay_pct: 0, platform_pct: 0, vat_pct: 0, vat_enabled: false }))
  }, [])

  // Debounce main search
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 400)
    return () => clearTimeout(t)
  }, [searchInput])

  // Shop suggestions: fetch as user types shop name
  useEffect(() => {
    if (!shopQuery.trim()) { setShopSuggestions([]); return }
    setShopSugLoading(true)
    const t = setTimeout(async () => {
      try {
        const res = await api.get(`/payments/admin/shops-search?search=${encodeURIComponent(shopQuery)}&limit=8`)
        setShopSuggestions(res.data.shops || [])
      } catch { setShopSuggestions([]) }
      finally { setShopSugLoading(false) }
    }, 300)
    return () => clearTimeout(t)
  }, [shopQuery])

  const fetchReport = useCallback(async () => {
    const id = ++fetchIdRef.current
    setLoading(true); setError('')
    try {
      const params = new URLSearchParams({
        page, limit: PER_PAGE,
        date_from: dateFrom,
        date_to:   dateTo,
        ...(search        && { search }),
        ...(methodFilter  && { method: methodFilter }),
      })
      const res = await api.get(`/payments/admin/payout-report?${params}`)
      if (id !== fetchIdRef.current) return
      setTransactions(res.data.transactions || [])
      setTotalPages(res.data.totalPages     || 1)
      setTotalCount(res.data.total          || 0)
      setSummary(res.data.summary || { gross: 0, delivery_total: 0, ipay_total: 0, platform_total: 0, seller_total: 0, count: 0 })
      setRefunds(res.data.refunds || [])
      setRefundSummary(res.data.refund_summary || { total_count:0, refunded_count:0, refunded_amount:0, pending_count:0, pending_amount:0 })
      // Fetch tips for the same period
      const tipParams = new URLSearchParams({ page: 1, limit: 9999, period: 'all',
        date_from: dateFrom, date_to: dateTo })
      try {
        const tipRes = await api.get(`/tips/admin/all?${tipParams}`)
        setTips(tipRes.data.tips || [])
        setTipSummary(tipRes.data.stats || { total_tips:0, total_amount:0, avg_tip:0, biggest_tip:0, unique_buyers:0, week_amount:0, month_amount:0 })
      } catch { setTips([]); setTipSummary({ total_tips:0, total_amount:0, avg_tip:0, biggest_tip:0, unique_buyers:0, week_amount:0, month_amount:0 }) }
    } catch {
      if (id !== fetchIdRef.current) return
      setError('Failed to load — showing mock data')
      const mock = Array.from({ length: 15 }, (_, i) => {
        const gross = Math.floor(Math.random() * 30000 + 5000)
        const df    = Math.floor(Math.random() * 3000 + 500)
        const b     = bd(gross, df)
        return {
          order_id:        1000 + i,
          payment_ref:     `TXN${Date.now()}${i}`,
          payment_method:  ['mtn','airtel','ipay'][i % 3],
          payment_number:  `078${String(i+1).padStart(7,'0')}`,
          payment_status:  'paid',
          amount:          gross,
          delivery_fee:    df,
          paid_at:         new Date(Date.now() - i * 86400000).toISOString(),
          buyer_name:      `Buyer ${i+1}`,
          buyer_phone:     `078${String(i+1).padStart(7,'0')}`,
          shop_name:       `Shop ${(i%4)+1}`,
          driver_name:     i % 4 === 0 ? null : `Driver ${(i%3)+1}`,
          driver_phone:    i % 4 === 0 ? null : `079${String(i+1).padStart(7,'0')}`,
          driver_company:  i % 4 === 0 ? null : 'Fast Logistics',
          vehicle_type:    i % 4 === 0 ? null : 'Motorcycle',
          delivery_status: i % 4 === 0 ? null : 'delivered',
          delivered_at:    i % 4 === 0 ? null : new Date(Date.now() - i * 86400000 + 3600000).toISOString(),
          ...b,
        }
      })
      setTransactions(mock)
      setTotalPages(3); setTotalCount(75)
      const totals = mock.reduce((acc, r) => {
        const b = bd(r.amount, r.delivery_fee)
        return {
          gross:          acc.gross + b.gross,
          delivery_total: acc.delivery_total + b.delivery_fee,
          ipay_total:     acc.ipay_total + b.ipay_fee,
          platform_total: acc.platform_total + b.platform_fee,
          seller_total:   acc.seller_total + b.seller_payout,
          count:          acc.count + 1,
        }
      }, { gross: 0, delivery_total: 0, ipay_total: 0, platform_total: 0, seller_total: 0, count: 0 })
      setSummary(totals)
      allRowsRef.current = mock
      setRefunds([])
      setRefundSummary({ total_count:0, refunded_count:0, refunded_amount:0, pending_count:0, pending_amount:0 })
      const mockTips = Array.from({ length: 8 }, (_, i) => ({
        tip_id: i+1, amount: [100,200,500,1000,2000][i%5],
        emoji: ['❤️','🔥','👏','💎','🎁'][i%5],
        buyer_name: `Buyer ${i+1}`, seller_name: `Seller ${(i%3)+1}`,
        shop_name: `Shop ${(i%3)+1}`, payment_method: ['mtn','airtel'][i%2],
        created_at: new Date(Date.now() - i*86400000).toISOString(),
      }))
      setTips(mockTips)
      setTipSummary({ total_tips:8, total_amount:4300, avg_tip:537, biggest_tip:2000, unique_buyers:6, week_amount:2200, month_amount:4300 })
    } finally { if (id === fetchIdRef.current) setLoading(false) }
  }, [page, dateFrom, dateTo, search, methodFilter])

  useEffect(() => { fetchReport() }, [fetchReport])
  useEffect(() => { setPage(1)    }, [dateFrom, dateTo, search, methodFilter])

  // ── Shop Report fetch ──────────────────────────────────────────────────────
  const fetchShopReport = useCallback(async () => {
    if (!selectedShop) return
    const id = ++shopFetchIdRef.current
    setShopLoading(true)
    try {
      const params = new URLSearchParams({
        shop_id: selectedShop.shop_id,
        page: shopPage, limit: PER_PAGE,
        date_from: dateFrom, date_to: dateTo,
      })
      const res = await api.get(`/payments/admin/shop-report?${params}`)
      if (id !== shopFetchIdRef.current) return
      setShopTxns(res.data.transactions || [])
      setShopTips(res.data.tips         || [])
      setShopTotalPages(res.data.totalPages || 1)
      setShopTotalCount(res.data.total      || 0)
      const sum = res.data.summary || {}
      setShopSummary({ gross:0, delivery_total:0, ipay_total:0, platform_total:0, seller_total:0, tips_gross_total:0, tips_ipay_total:0, tips_platform_total:0, tips_total:0, tips_count:0, final_payout:0, order_count:0, ipay_pct: 0, ...sum })
      setShopTier({
        tier:                   res.data.tier                   ?? 'bronze',
        tier_discount:          res.data.tier_discount          ?? 0,
        platform_pct_base:      res.data.platform_pct_base      ?? 0,
        platform_pct_effective: res.data.platform_pct_effective ?? 0,
        is_overridden:          res.data.is_overridden          ?? false,
      })
    } catch {
      if (id !== shopFetchIdRef.current) return
      setShopTxns([]); setShopTips([])
    } finally {
      if (id === shopFetchIdRef.current) setShopLoading(false)
    }
  }, [selectedShop, shopPage, dateFrom, dateTo])

  const handleShopExportCSV = async () => {
    if (!selectedShop) return
    setShopExporting(true)
    try {
      // Fetch all pages for a complete export
      const params = new URLSearchParams({
        shop_id: selectedShop.shop_id,
        page: 1, limit: 9999,
        date_from: dateFrom, date_to: dateTo,
      })
      const res = await api.get(`/payments/admin/shop-report?${params}`)
      const exportTierInfo = res.data.tier ? {
        tier:                   res.data.tier,
        tier_discount:          res.data.tier_discount          ?? 0,
        platform_pct_base:      res.data.platform_pct_base      ?? 0,
        platform_pct_effective: res.data.platform_pct_effective ?? 0,
        is_overridden:          res.data.is_overridden          ?? false,
      } : shopTier
      exportShopReportCSV({
        shopName: selectedShop.shop_name,
        feeConfig,
        txns:     res.data.transactions || shopTxns,
        tips:     res.data.tips         || shopTips,
        summary:  res.data.summary      || shopSummary,
        dateFrom, dateTo,
        tierInfo: exportTierInfo,
      })
    } catch {
      exportShopReportCSV({ shopName: selectedShop.shop_name, txns: shopTxns, tips: shopTips, summary: shopSummary, dateFrom, dateTo, feeConfig, tierInfo: shopTier })
    } finally {
      setShopExporting(false)
    }
  }

  useEffect(() => { fetchShopReport() }, [fetchShopReport])
  useEffect(() => { setShopPage(1) }, [selectedShop, dateFrom, dateTo])

  // ── Pending Payments fetch ──────────────────────────────────────────────────
  const fetchPending = useCallback(async () => {
    const id = ++pendingFetchIdRef.current
    setPendingLoading(true)
    try {
      const params = new URLSearchParams({ page: pendingPage, limit: PER_PAGE })
      const res = await api.get(`/payments/admin/pending?${params}`)
      if (id !== pendingFetchIdRef.current) return
      setPendingPayments(res.data.payments || [])
      setPendingTotalPages(res.data.totalPages || 1)
      setPendingTotalCount(res.data.total || 0)
      setPendingSummary(res.data.summary || { count: 0, total_amount: 0 })
      if (res.data.expiry_minutes) setPendingExpiryMinutes(res.data.expiry_minutes)
    } catch {
      if (id !== pendingFetchIdRef.current) return
      const mock = Array.from({ length: 7 }, (_, i) => ({
        order_id:       5000 + i,
        payment_method: ['mtn', 'airtel', 'ipay'][i % 3],
        payment_number: `078${String(i + 1).padStart(7, '0')}`,
        amount:         [8000, 15000, 4500, 22000, 9500, 3200, 18700][i],
        buyer_name:     `Buyer ${i + 1}`,
        buyer_phone:    `078${String(i + 1).padStart(7, '0')}`,
        shop_name:      `Shop ${(i % 3) + 1}`,
        created_at:     new Date(Date.now() - i * 3600000 * 1.5).toISOString(),
        payment_status: 'pending',
      }))
      setPendingPayments(mock)
      setPendingTotalPages(1)
      setPendingTotalCount(mock.length)
      setPendingSummary({ count: mock.length, total_amount: mock.reduce((s, r) => s + r.amount, 0) })
    } finally {
      if (id === pendingFetchIdRef.current) setPendingLoading(false)
    }
  }, [pendingPage])

  useEffect(() => { fetchPending() }, [fetchPending])

  const handleExportCSV = async () => {
    setExporting(true)
    try {
      // Fetch all rows for this period (no pagination)
      const params = new URLSearchParams({
        page: 1, limit: 9999,
        date_from: dateFrom, date_to: dateTo,
        ...(search       && { search }),
        ...(methodFilter && { method: methodFilter }),
      })
      const res  = await api.get(`/payments/admin/payout-report?${params}`)
      const rows = res.data.transactions || transactions
      exportCSV(rows, `${dateFrom}_${dateTo}`, feeConfig)
    } catch {
      exportCSV(transactions, `${dateFrom}_${dateTo}`, feeConfig)
    } finally {
      setExporting(false)
    }
  }

  const handleExportPDF = async () => {
    setPdfExporting(true)
    try {
      const params = new URLSearchParams({
        date_from: dateFrom, date_to: dateTo,
        ...(methodFilter && { method: methodFilter }),
      })
      const res = await api.get(`/payments/admin/payout-report/pdf?${params}`, {
        responseType: 'blob',
      })
      const url      = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      const a        = document.createElement('a')
      a.href         = url
      a.download     = `kudos-payout-${dateFrom}-to-${dateTo}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      alert('Failed to generate PDF. Make sure pdfkit is installed on the server.')
    } finally {
      setPdfExporting(false)
    }
  }

  const periodLabel = `${fmt(dateFrom)} – ${fmt(dateTo)}`
  const iPctLabel  = feeConfig ? +(feeConfig.ipay_pct     * 100).toFixed(2).replace(/\.?0+$/, '') : '…'
  const pPctLabel  = feeConfig ? +(feeConfig.platform_pct * 100).toFixed(2).replace(/\.?0+$/, '') : '…'
  const vatEnabled = !!(feeConfig?.vat_enabled && feeConfig?.vat_pct > 0)
  const vatMul     = vatEnabled ? feeConfig.vat_pct / 100 : 0
  const bd = (gross, df = 0) => breakdown(gross, df, feeConfig?.ipay_pct ?? 0, feeConfig?.platform_pct ?? 0, vatMul)
  const shopBd = (gross, df = 0) => breakdown(gross, df, feeConfig?.ipay_pct ?? 0, shopTier?.platform_pct_effective ?? feeConfig?.platform_pct ?? 0, vatMul)

  return (
    <div style={{ padding: '28px 32px 60px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px' }}>Payout Report</div>
          <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>Monthly breakdown for bank transfer. Shows seller payouts after all fees.</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {refundTab === 'refunds' && (
            <button
              onClick={() => exportRefundsCSV(refunds, `${dateFrom}_${dateTo}`)}
              disabled={refunds.length === 0}
              style={{ height: 38, padding: '0 16px', borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', color: '#111317', fontSize: 12, fontWeight: 700, cursor: refunds.length === 0 ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', gap: 7 }}>
              ⬇ Export Refunds CSV
            </button>
          )}
          {refundTab === 'tips' && (
            <button
              onClick={() => exportTipsCSV(tips, `${dateFrom}_${dateTo}`)}
              disabled={tips.length === 0}
              style={{ height: 38, padding: '0 16px', borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', color: '#111317', fontSize: 12, fontWeight: 700, cursor: tips.length === 0 ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', gap: 7 }}>
              ⬇ Export Tips CSV
            </button>
          )}
          <button
            onClick={handleExportPDF}
            disabled={pdfExporting || transactions.length === 0}
            style={{ height: 38, padding: '0 16px', borderRadius: 8, border: '1px solid #e9e9e4', background: pdfExporting ? '#f3f3f0' : '#fff', color: '#111317', fontSize: 12, fontWeight: 700, cursor: pdfExporting ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', gap: 7 }}>
            {pdfExporting ? '⏳ Generating…' : '📄 Export PDF'}
          </button>
          <button
            onClick={handleExportCSV}
            disabled={exporting || transactions.length === 0}
            style={{ height: 38, padding: '0 16px', borderRadius: 8, border: 'none', background: exporting ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 12, fontWeight: 700, cursor: exporting ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', gap: 7, boxShadow: '0 2px 8px rgba(232,68,10,0.2)' }}>
            {exporting ? '⏳ Exporting…' : '⬇ Export CSV'}
          </button>
        </div>
      </div>

      {/* Date range + filters */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '12px 16px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: '#9a9a94', fontWeight: 600 }}>From</span>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            style={{ border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 10px', fontSize: 12, fontWeight: 600, color: '#111317', outline: 'none', fontFamily: "'JetBrains Mono',monospace" }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: '#9a9a94', fontWeight: 600 }}>To</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            style={{ border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 10px', fontSize: 12, fontWeight: 600, color: '#111317', outline: 'none', fontFamily: "'JetBrains Mono',monospace" }} />
        </div>

        {/* Month shortcuts */}
        {[0, 1, 2].map(monthsAgo => {
          const d    = new Date(now.getFullYear(), now.getMonth() - monthsAgo, 1)
          const from = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`
          const to   = new Date(d.getFullYear(), d.getMonth()+1, 0).toISOString().slice(0,10)
          const lbl  = monthsAgo === 0 ? 'This month' : d.toLocaleString('en-GB', { month: 'short', year: 'numeric' })
          const active = dateFrom === from && dateTo === to
          return (
            <button key={monthsAgo} onClick={() => { setDateFrom(from); setDateTo(to) }}
              style={{ padding: '6px 12px', borderRadius: 6, border: `1px solid ${active ? '#111317' : '#e9e9e4'}`, background: active ? '#111317' : '#fff', color: active ? '#fff' : '#6b7280', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
              {lbl}
            </button>
          )
        })}

        <div style={{ flex: 1 }} />

        {/* Search */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f6f6f3', border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 11px' }}>
          <span style={{ fontSize: 13, color: '#9a9a94' }}>🔍</span>
          <input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Buyer, shop, ref…"
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 12, width: 160, fontFamily: "'Plus Jakarta Sans',sans-serif" }} />
          {searchInput && <span onClick={() => { setSearchInput(''); setSearch('') }} style={{ cursor: 'pointer', color: '#9a9a94', fontSize: 14 }}>×</span>}
        </div>

        <select value={methodFilter} onChange={e => setMethodFilter(e.target.value)}
          style={{ border: '1px solid #e9e9e4', borderRadius: 7, padding: '8px 11px', fontSize: 12, fontWeight: 600, color: '#111317', background: '#fff', cursor: 'pointer', outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
          <option value="">All Methods</option>
          <option value="mtn">MTN MoMo</option>
          <option value="airtel">Airtel Money</option>
          <option value="ipay">iPay / Irembo</option>
        </select>
      </div>

      {/* Period label */}
      <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginBottom: 16 }}>
        Report period: <b style={{ color: '#111317' }}>{periodLabel}</b> · {totalCount.toLocaleString()} paid transactions
      </div>

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
        <SummaryCard label="Gross Collected"    value={money(summary.gross)}                   accent="#111317" bold />
        <SummaryCard label="Driver Delivery"    value={money(summary.driver_delivery_total)}  accent="#b45309" sub="→ to drivers" />
        {summary.seller_delivery_gross > 0 && (
          <SummaryCard label="Self-Delivery Gross" value={money(summary.seller_delivery_gross)} accent="#1d4ed8" sub="charged to buyer" />
        )}
        {summary.seller_delivery_gross > 0 && (
          <SummaryCard label="Self-Delivery Net"   value={money(summary.seller_delivery_net)}   accent="#0369a1" sub="→ after commission" />
        )}
        {summary.vat_total > 0 && (
          <SummaryCard label="VAT Collected"    value={money(summary.vat_total)}               accent="#0369a1" sub="→ collected for govt" />
        )}
        <SummaryCard label={`iPay Fee (${iPctLabel}%)`}     value={money(summary.ipay_total)}     accent="#b91c1c" sub="→ to iPay/Irembo" />
        <SummaryCard label={`Platform Fee (${pPctLabel}%)`} value={money(summary.platform_total)} accent="#7c3aed" sub="→ your commission" />
        <SummaryCard label="Seller Payout"      value={money(Number(summary.seller_total) + Number(summary.seller_delivery_net || 0))}  accent="#15803d" sub="→ send to bank (incl. self-delivery net)" bold />
      </div>
      {/* Refund impact row */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <SummaryCard
          label="Total Refunded"
          value={money(refundSummary.refunded_amount)}
          accent="#b91c1c"
          sub={`${refundSummary.refunded_count} completed · ${refundSummary.pending_count} pending`}
        />
        <SummaryCard
          label="Net Seller Payout"
          value={money(Number(summary.seller_total) + Number(summary.seller_delivery_net || 0) - refundSummary.refunded_amount)}
          accent="#15803d"
          sub="After refunds deducted (incl. self-delivery)"
          bold
        />
        <SummaryCard
          label="Refund Requests"
          value={refundSummary.total_count}
          accent={refundSummary.pending_count > 0 ? '#b45309' : '#9a9a94'}
          sub={refundSummary.pending_count > 0 ? `⚠ ${refundSummary.pending_count} need action` : 'All resolved'}
        />
        <div style={{ flex: 2 }} />
      </div>

      {/* Tips + Final Payout row */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <SummaryCard label="Total Tips"         value={money(tipSummary.total_amount)} accent="#e8440a" sub={`${tipSummary.total_tips} tips received`} bold />
        <SummaryCard label="Avg Tip"            value={money(tipSummary.avg_tip)}      accent="#374151" sub="per tip transaction" />
        <SummaryCard label="Biggest Tip"        value={money(tipSummary.biggest_tip)}  accent="#7c3aed" sub="single largest tip" />
        <SummaryCard label="Unique Tippers"     value={tipSummary.unique_buyers}       accent="#111317" sub="distinct buyers" />
        <SummaryCard
          label="Final Payout to Sellers"
          value={money(Number(summary.seller_total) + Number(summary.seller_delivery_net || 0) - refundSummary.refunded_amount + tipSummary.total_amount)}
          accent="#15803d"
          sub="Payout + Delivery − Refunds + Tips"
          bold
        />
      </div>

      {/* Fee formula reminder */}
      <div style={{ background: '#f9f9f7', border: '1px solid #e9e9e4', borderLeft: '3px solid #e8440a', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 12, color: '#374151', fontFamily: "'JetBrains Mono',monospace" }}>
        {vatEnabled
          ? `📐 Formula: Payout = Gross − iPay(${iPctLabel}%) − Platform(${pPctLabel}%) · VAT(${feeConfig.vat_pct}%) collected separately · Delivery goes to driver · Final = Payout − Refunds + Tips`
          : `📐 Formula: Payout = Gross − iPay(${iPctLabel}%) − Platform(${pPctLabel}%) · Delivery goes to driver separately · Final = Payout − Refunds + Tips`
        }
      </div>

      {/* Tab switcher */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: 5, width: 'fit-content' }}>
        {[
          ['transactions', `💳 Transactions (${totalCount.toLocaleString()})`],
          ['pending',      `⏳ Pending (${pendingTotalCount})`],
          ['refunds',      `↩ Refunds (${refundSummary.total_count})`],
          ['tips',         `💝 Tips (${tipSummary.total_tips})`],
          ['shop',         `🏪 Shop Report`],
        ].map(([id, label]) => (
          <button key={id} onClick={() => setRefundTab(id)}
            style={{ padding: '7px 18px', borderRadius: 7, border: 'none', background: refundTab === id ? '#111317' : 'transparent', color: refundTab === id ? '#fff' : '#9a9a94', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", transition: 'all 0.15s', whiteSpace: 'nowrap' }}>
            {label}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 16px', marginBottom: 14, fontSize: 13, color: '#b91c1c' }}>⚠ {error}</div>
      )}

      {/* Transactions table */}
      {refundTab === 'transactions' && (
      <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '0.6fr 1.6fr 1.2fr 0.9fr 0.9fr 0.8fr 0.8fr 0.8fr 1fr 0.8fr', padding: '10px 18px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
          {['#','Buyer / Shop','Driver','Method','Gross','Delivery',`iPay ${iPctLabel}%`,`Pltf ${pPctLabel}%`,'Seller Payout','Date'].map(h => (
            <div key={h} style={{ fontSize: 9, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>Loading report…</div>
        ) : transactions.length === 0 ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>
            No paid transactions found for this period.
          </div>
        ) : (
          transactions.map((t, i) => {
            // Use backend-computed values (VAT-aware). bd() only for mock data fallback.
            const gross   = Number(t.amount || t.total_amount || 0)
            const ipay    = t.ipay_fee     != null ? Number(t.ipay_fee)     : bd(gross, t.delivery_fee).ipay_fee
            const plat    = t.platform_fee != null ? Number(t.platform_fee) : bd(gross, t.delivery_fee).platform_fee
            const payout  = t.seller_payout != null ? Number(t.seller_payout) : bd(gross, t.delivery_fee).seller_payout
            return (
              <div key={t.order_id || i}
                onClick={() => setSelected(t)}
                style={{ display: 'grid', gridTemplateColumns: '0.6fr 1.6fr 1.2fr 0.9fr 0.9fr 0.8fr 0.8fr 0.8fr 1fr 0.8fr', padding: '11px 18px', borderBottom: i < transactions.length - 1 ? '1px solid #f3f3f0' : 'none', cursor: 'pointer', transition: 'background 0.1s', alignItems: 'center' }}
                onMouseEnter={e => e.currentTarget.style.background = '#fafaf8'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                {/* Order # */}
                <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>#{t.order_id}</div>

                {/* Buyer + shop */}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.buyer_name || 'Anonymous'}</div>
                  <div style={{ fontSize: 10, color: '#9a9a94', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.shop_name || '—'}</div>
                </div>

                {/* Driver */}
                <div style={{ minWidth: 0 }}>
                  {t.driver_name || t.driver_phone ? (
                    <>
                      <div style={{ fontSize: 11, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#15803d' }}>{t.driver_name || '—'}</div>
                      <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{t.driver_phone || '—'}</div>
                    </>
                  ) : (
                    <span style={{ fontSize: 11, color: '#d1d5db', fontStyle: 'italic' }}>No driver</span>
                  )}
                </div>

                {/* Method */}
                <div style={{ fontSize: 11, fontWeight: 600, fontFamily: "'JetBrains Mono',monospace", color: '#374151' }}>
                  {t.payment_method?.toUpperCase() || '—'}
                </div>

                {/* Amounts */}
                <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>{moneyN(gross)}</div>
                <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", color: '#b45309' }}>{moneyN(t.delivery_fee)}</div>
                <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", color: '#b91c1c' }}>{moneyN(ipay)}</div>
                <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", color: '#7c3aed' }}>{moneyN(plat)}</div>
                <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", fontWeight: 800, color: '#15803d' }}>{moneyN(payout)}</div>

                {/* Date */}
                <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{fmt(t.paid_at || t.created_at)}</div>
              </div>
            )
          })
        )}

        {/* Totals footer */}
        {!loading && transactions.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: '0.6fr 1.6fr 1.2fr 0.9fr 0.9fr 0.8fr 0.8fr 0.8fr 1fr 0.8fr', padding: '12px 18px', borderTop: '2px solid #e9e9e4', background: '#f9f9f7', alignItems: 'center' }}>
            <div />
            <div style={{ fontSize: 11, fontWeight: 800, color: '#111317' }}>Page Total ({transactions.length})</div>
            <div />
            <div />
            <div style={{ fontSize: 12, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace" }}>{moneyN(transactions.reduce((s, t) => s + Number(t.amount || t.total_amount || 0), 0))}</div>
            <div style={{ fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#b45309' }}>{moneyN(transactions.reduce((s, t) => s + Number(t.delivery_fee || 0), 0))}</div>
            <div style={{ fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#b91c1c' }}>{moneyN(transactions.reduce((s, t) => s + (t.ipay_fee     != null ? Number(t.ipay_fee)     : bd(t.amount || t.total_amount, t.delivery_fee).ipay_fee),    0))}</div>
            <div style={{ fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#7c3aed' }}>{moneyN(transactions.reduce((s, t) => s + (t.platform_fee  != null ? Number(t.platform_fee)  : bd(t.amount || t.total_amount, t.delivery_fee).platform_fee), 0))}</div>
            <div style={{ fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#15803d' }}>{moneyN(transactions.reduce((s, t) => s + (t.seller_payout != null ? Number(t.seller_payout) : bd(t.amount || t.total_amount, t.delivery_fee).seller_payout), 0))}</div>
            <div />
          </div>
        )}
      </div>

      )}   {/* end refundTab === transactions */}

      {/* Refunds table */}
      {refundTab === 'refunds' && (
        <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
          {/* Table header */}
          <div style={{ display: 'grid', gridTemplateColumns: '0.5fr 1.4fr 1.2fr 0.9fr 0.9fr 0.9fr 1fr 0.9fr', padding: '10px 18px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
            {['#','Buyer / Shop','Reason','Amount','Method','Status','Ref','Requested'].map(h => (
              <div key={h} style={{ fontSize: 9, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
            ))}
          </div>

          {refunds.length === 0 ? (
            <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>
              No refund requests in this period.
            </div>
          ) : refunds.map((r, i) => {
            const RS = {
              pending:    { bg: '#fffbeb', color: '#b45309', label: 'Pending'    },
              processing: { bg: '#eff6ff', color: '#1d4ed8', label: 'Processing' },
              approved:   { bg: '#f0fdf4', color: '#15803d', label: 'Approved'   },
              refunded:   { bg: '#f0fdf4', color: '#15803d', label: 'Refunded ✓' },
              rejected:   { bg: '#fef2f2', color: '#b91c1c', label: 'Rejected'   },
              cancelled:  { bg: '#f3f4f6', color: '#6b7280', label: 'Cancelled'  },
            }
            const rs = RS[r.refund_status] || RS.pending
            return (
              <div key={r.refund_id || i}
                style={{ display: 'grid', gridTemplateColumns: '0.5fr 1.4fr 1.2fr 0.9fr 0.9fr 0.9fr 1fr 0.9fr', padding: '11px 18px', borderBottom: i < refunds.length - 1 ? '1px solid #f3f3f0' : 'none', alignItems: 'center', background: r.refund_status === 'refunded' ? 'rgba(240,253,244,0.5)' : r.refund_status === 'pending' ? 'rgba(255,251,235,0.5)' : 'transparent' }}>

                {/* Order # */}
                <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>#{r.order_id}</div>

                {/* Buyer + shop */}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.buyer_name || 'Anonymous'}</div>
                  <div style={{ fontSize: 10, color: '#9a9a94', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.shop_name || '—'}</div>
                </div>

                {/* Reason */}
                <div style={{ fontSize: 11, color: '#374151', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.reason_label || r.reason || '—'}
                </div>

                {/* Amount */}
                <div style={{ fontSize: 12, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: r.refund_status === 'refunded' ? '#b91c1c' : '#374151' }}>
                  {r.refund_status === 'refunded' ? `- ${moneyN(r.refund_amount)}` : moneyN(r.refund_amount)}
                </div>

                {/* Method */}
                <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", color: '#374151', fontWeight: 600 }}>
                  {r.payment_method?.toUpperCase() || '—'}
                </div>

                {/* Status badge */}
                <div>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: rs.bg, color: rs.color, fontSize: 9, fontWeight: 700, padding: '3px 7px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: "'JetBrains Mono',monospace" }}>
                    <span style={{ width: 4, height: 4, borderRadius: '50%', background: rs.color }} />
                    {rs.label}
                  </span>
                </div>

                {/* Refund ref */}
                <div style={{ fontSize: 10, color: r.refund_ref ? '#111317' : '#d1d5db', fontFamily: "'JetBrains Mono',monospace", fontStyle: r.refund_ref ? 'normal' : 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.refund_ref || 'Pending'}
                </div>

                {/* Date */}
                <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>
                  {fmt(r.requested_at)}
                </div>
              </div>
            )
          })}

          {/* Refund totals footer */}
          {refunds.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '0.5fr 1.4fr 1.2fr 0.9fr 0.9fr 0.9fr 1fr 0.9fr', padding: '12px 18px', borderTop: '2px solid #e9e9e4', background: '#f9f9f7', alignItems: 'center' }}>
              <div />
              <div style={{ fontSize: 11, fontWeight: 800, color: '#111317' }}>Total ({refunds.length})</div>
              <div />
              <div style={{ fontSize: 12, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#b91c1c' }}>
                - {moneyN(refundSummary.refunded_amount)} refunded
              </div>
              <div />
              <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>
                {refundSummary.pending_count > 0 && <span style={{ color: '#b45309' }}>{refundSummary.pending_count} pending</span>}
              </div>
              <div />
              <div />
            </div>
          )}
        </div>
      )}

      {/* ── Tips Tab ── */}
      {refundTab === 'tips' && (
        <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '0.5fr 1.1fr 1.1fr 0.6fr 0.8fr 0.8fr 0.6fr 0.9fr', padding: '10px 18px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
            {['#','Buyer','Shop / Seller','Emoji','Gross','Net Payout','Method','Date'].map(h => (
              <div key={h} style={{ fontSize: 9, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
            ))}
          </div>

          {tips.length === 0 ? (
            <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>No tips in this period.</div>
          ) : tips.map((t, i) => {
            const gross = Number(t.amount || 0)
            const net   = Number(t.net_amount ?? (gross - Number(t.ipay_fee || 0) - Number(t.platform_fee || 0)))
            return (
            <div key={t.tip_id || i}
              style={{ display: 'grid', gridTemplateColumns: '0.5fr 1.1fr 1.1fr 0.6fr 0.8fr 0.8fr 0.6fr 0.9fr', padding: '11px 18px', borderBottom: i < tips.length - 1 ? '1px solid #f3f3f0' : 'none', alignItems: 'center' }}>
              <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>#{t.tip_id}</div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.buyer_name || '—'}</div>
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.shop_name || '—'}</div>
                <div style={{ fontSize: 10, color: '#9a9a94', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.seller_name || '—'}</div>
              </div>
              <div style={{ fontSize: 24, lineHeight: 1 }}>{t.emoji || '💰'}</div>
              <div style={{ fontSize: 12, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#e8440a' }}>{moneyN(gross)}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#15803d' }}>{moneyN(net)}</div>
                {(Number(t.ipay_fee || 0) > 0 || Number(t.platform_fee || 0) > 0) && (
                  <div style={{ fontSize: 8, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>−iPay {moneyN(t.ipay_fee)} −plat {moneyN(t.platform_fee)}</div>
                )}
              </div>
              <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600, color: '#374151' }}>{(t.payment_method || '—').toUpperCase()}</div>
              <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{fmt(t.created_at)}</div>
            </div>
          )})}

          {tips.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '0.5fr 1.1fr 1.1fr 0.6fr 0.8fr 0.8fr 0.6fr 0.9fr', padding: '12px 18px', borderTop: '2px solid #e9e9e4', background: '#f9f9f7', alignItems: 'center' }}>
              <div /><div style={{ fontSize: 11, fontWeight: 800 }}>Total ({tips.length})</div>
              <div /><div />
              <div style={{ fontSize: 12, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#e8440a' }}>
                {moneyN(tips.reduce((s, t) => s + Number(t.amount || 0), 0))}
              </div>
              <div style={{ fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#15803d' }}>
                {moneyN(tips.reduce((s, t) => s + Number(t.net_amount ?? (Number(t.amount||0) - Number(t.ipay_fee||0) - Number(t.platform_fee||0))), 0))}
              </div>
              <div /><div />
            </div>
          )}
        </div>
      )}

      {/* ── Pending Payments Tab ── */}
      {refundTab === 'pending' && (
        <div>
          {/* Summary strip */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <SummaryCard
              label="Pending Payments"
              value={pendingTotalCount}
              accent={pendingTotalCount > 0 ? '#b45309' : '#9a9a94'}
              sub={pendingTotalCount > 0 ? 'Awaiting confirmation' : 'All clear'}
            />
            <SummaryCard
              label="Pending Value"
              value={money(pendingSummary.total_amount)}
              accent="#b45309"
              sub="Total amount at risk"
              bold
            />
            <SummaryCard
              label="Expiry Window"
              value={`${pendingExpiryMinutes} min`}
              accent="#6b7280"
              sub="Auto-expired beyond this"
            />
            <div style={{ flex: 2 }} />
          </div>

          <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
            {/* Table header */}
            <div style={{ display: 'grid', gridTemplateColumns: '0.6fr 1.6fr 1fr 0.9fr 1.3fr 0.9fr 0.8fr', padding: '10px 18px', borderBottom: '1px solid #e9e9e4', background: '#fffbeb' }}>
              {['#', 'Buyer / Shop', 'Amount', 'Method', 'Phone', 'Waiting', 'Created'].map(h => (
                <div key={h} style={{ fontSize: 9, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
              ))}
            </div>

            {pendingLoading ? (
              <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>Loading pending payments…</div>
            ) : pendingPayments.length === 0 ? (
              <div style={{ padding: '48px 0', textAlign: 'center' }}>
                <div style={{ fontSize: 28, marginBottom: 10 }}>✅</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#111317', marginBottom: 4 }}>No pending payments</div>
                <div style={{ fontSize: 12, color: '#9a9a94' }}>All payments have been processed.</div>
              </div>
            ) : (
              pendingPayments.map((p, i) => {
                const minsWaiting = Math.floor((Date.now() - new Date(p.created_at)) / 60000)
                const isStale = minsWaiting >= pendingExpiryMinutes
                return (
                  <div key={p.order_id || i}
                    style={{ display: 'grid', gridTemplateColumns: '0.6fr 1.6fr 1fr 0.9fr 1.3fr 0.9fr 0.8fr', padding: '11px 18px', borderBottom: i < pendingPayments.length - 1 ? '1px solid #f3f3f0' : 'none', alignItems: 'center', background: isStale ? 'rgba(255,251,235,0.6)' : 'transparent' }}>

                    {/* Order # */}
                    <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>#{p.order_id}</div>

                    {/* Buyer / Shop */}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.buyer_name || 'Anonymous'}</div>
                      <div style={{ fontSize: 10, color: '#9a9a94', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.shop_name || '—'}</div>
                    </div>

                    {/* Amount */}
                    <div style={{ fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#111317' }}>{moneyN(p.amount)}</div>

                    {/* Method */}
                    <div style={{ fontSize: 11, fontWeight: 600, fontFamily: "'JetBrains Mono',monospace", color: '#374151' }}>
                      {(p.payment_method || '—').toUpperCase()}
                    </div>

                    {/* Phone */}
                    <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", color: '#374151' }}>
                      {p.payment_number || p.buyer_phone || '—'}
                    </div>

                    {/* Waiting */}
                    <div>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: isStale ? '#fffbeb' : '#f9f9f7', color: isStale ? '#b45309' : '#6b7280', fontSize: 10, fontWeight: 700, padding: '3px 7px', borderRadius: 4, fontFamily: "'JetBrains Mono',monospace", border: `1px solid ${isStale ? '#fcd34d' : '#e9e9e4'}` }}>
                        {isStale && <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#b45309', display: 'inline-block' }} />}
                        {timeAgo(p.created_at)}
                      </span>
                    </div>

                    {/* Created */}
                    <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{fmt(p.created_at)}</div>
                  </div>
                )
              })
            )}

            {/* Footer total */}
            {!pendingLoading && pendingPayments.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: '0.6fr 1.6fr 1fr 0.9fr 1.3fr 0.9fr 0.8fr', padding: '12px 18px', borderTop: '2px solid #e9e9e4', background: '#fffbeb', alignItems: 'center' }}>
                <div />
                <div style={{ fontSize: 11, fontWeight: 800, color: '#92400e' }}>Total ({pendingPayments.length})</div>
                <div style={{ fontSize: 12, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#b45309' }}>
                  {moneyN(pendingPayments.reduce((s, p) => s + Number(p.amount || 0), 0))}
                </div>
                <div /><div /><div /><div />
              </div>
            )}
          </div>

          {/* Pagination */}
          {pendingTotalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>
                Page {pendingPage} of {pendingTotalPages} · {pendingTotalCount.toLocaleString()} pending
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <PagBtn label="← Prev" disabled={pendingPage === 1}                  onClick={() => setPendingPage(p => p - 1)} />
                {Array.from({ length: Math.min(5, pendingTotalPages) }, (_, i) => {
                  const p = Math.max(1, Math.min(pendingPage - 2, pendingTotalPages - 4)) + i
                  return <PagBtn key={p} label={p} active={p === pendingPage} onClick={() => setPendingPage(p)} />
                })}
                <PagBtn label="Next →" disabled={pendingPage === pendingTotalPages} onClick={() => setPendingPage(p => p + 1)} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Shop Report Tab ── */}
      {refundTab === 'shop' && (
        <div>
          {/* Shop picker */}
          <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '16px 18px', marginBottom: 16, position: 'relative' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#111317', marginBottom: 10 }}>Select a Shop</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f6f6f3', border: '1px solid #e9e9e4', borderRadius: 7, padding: '8px 12px' }}>
              <span style={{ fontSize: 14, color: '#9a9a94' }}>🔍</span>
              <input
                value={shopQuery}
                onChange={e => { setShopQuery(e.target.value); if (!e.target.value) { setSelectedShop(null); setShopTxns([]) } }}
                placeholder="Type shop name…"
                style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, flex: 1, fontFamily: "'Plus Jakarta Sans',sans-serif" }}
              />
              {shopQuery && <span onClick={() => { setShopQuery(''); setSelectedShop(null); setShopTxns([]); setShopSuggestions([]) }} style={{ cursor: 'pointer', color: '#9a9a94', fontSize: 16 }}>×</span>}
              {shopSugLoading && <span style={{ fontSize: 11, color: '#9a9a94' }}>…</span>}
            </div>

            {/* Suggestions dropdown */}
            {shopSuggestions.length > 0 && !selectedShop && (
              <div style={{ position: 'absolute', left: 18, right: 18, top: 'calc(100% - 8px)', background: '#fff', border: '1px solid #e9e9e4', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', zIndex: 100, overflow: 'hidden' }}>
                {shopSuggestions.map(sh => (
                  <div
                    key={sh.shop_id}
                    onClick={() => { setSelectedShop(sh); setShopQuery(sh.shop_name); setShopSuggestions([]) }}
                    style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f3f3f0', display: 'flex', alignItems: 'center', gap: 10 }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fafaf8'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                  >
                    <div style={{ width: 30, height: 30, borderRadius: 7, background: avatarBg(sh.shop_name || ''), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0 }}>
                      {initials(sh.shop_name)}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>{sh.shop_name}</div>
                      <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{sh.category || '—'} · {sh.owner_name || '—'}</div>
                    </div>
                    <div style={{ marginLeft: 'auto' }}>
                      <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: sh.status === 1 ? '#f0fdf4' : '#fef2f2', color: sh.status === 1 ? '#15803d' : '#b91c1c' }}>
                        {sh.status === 1 ? 'Active' : sh.status === 2 ? 'Pending' : 'Suspended'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Shop summary + table */}
          {selectedShop && (
            <>
              {/* Summary header with export */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#111317', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {shopSummary.shop_name || selectedShop.shop_name}
                  <span style={{ fontSize: 11, color: '#9a9a94', fontWeight: 400, fontFamily: "'JetBrains Mono',monospace" }}>
                    {fmt(dateFrom)} – {fmt(dateTo)} · {shopTotalCount.toLocaleString()} orders
                  </span>
                  {shopTier && <TierBadge tier={shopTier.tier} />}
                </div>
                <button
                  onClick={handleShopExportCSV}
                  disabled={shopExporting || (shopTxns.length === 0 && shopTips.length === 0)}
                  style={{ height: 34, padding: '0 14px', borderRadius: 7, border: 'none', background: shopExporting ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 11, fontWeight: 700, cursor: shopExporting ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 2px 8px rgba(232,68,10,0.2)' }}>
                  {shopExporting ? '⏳ Exporting…' : '⬇ Export CSV'}
                </button>
              </div>

              {/* Summary cards row 1 — revenue & fees */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                <SummaryCard label="Orders"          value={shopSummary.order_count.toLocaleString()} accent="#111317" />
                <SummaryCard label="Gross Collected" value={money(shopSummary.gross)}                 accent="#111317" bold />
                {shopSummary.vat_total > 0 && (
                  <SummaryCard label="VAT Collected" value={money(shopSummary.vat_total)} accent="#0369a1" sub="→ collected for govt" />
                )}
                <SummaryCard label={`iPay Fee (${iPctLabel}%)`}  value={money(shopSummary.ipay_total)}     accent="#b91c1c" />
                <SummaryCard
                  label={`Platform (${shopTier?.platform_pct_effective != null ? (shopTier.platform_pct_effective * 100).toFixed(0) : pPctLabel}%)`}
                  value={money(shopSummary.platform_total)}
                  accent="#7c3aed"
                  sub={shopTier?.tier_discount > 0 ? `base ${(shopTier.platform_pct_base * 100).toFixed(0)}% − ${(shopTier.tier_discount * 100).toFixed(0)}pp tier discount` : undefined}
                />
              </div>
              {/* Summary cards row 2 — delivery split */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                {shopSummary.driver_delivery_total > 0 && (
                  <SummaryCard label="Driver Delivery"         value={money(shopSummary.driver_delivery_total)}   accent="#b45309" sub="→ paid to driver" />
                )}
                {shopSummary.seller_delivery_gross > 0 && (
                  <SummaryCard label="Seller Delivery (Gross)" value={money(shopSummary.seller_delivery_gross)}   accent="#0369a1" sub="seller handled delivery" />
                )}
                {shopSummary.seller_delivery_commission > 0 && (
                  <SummaryCard label={`Delivery Commission (${shopSummary.driver_comm_pct}%)`} value={money(shopSummary.seller_delivery_commission)} accent="#e8440a" sub="platform cut" />
                )}
                {shopSummary.seller_delivery_payout > 0 && (
                  <SummaryCard label="Seller Delivery Net"     value={money(shopSummary.seller_delivery_payout)}  accent="#1d4ed8" sub="delivery fee seller keeps" />
                )}
                <SummaryCard label="Product Payout"           value={money(shopSummary.gross - shopSummary.ipay_total - shopSummary.platform_total)} accent="#15803d" />
                <SummaryCard label="Seller Total Payout"      value={money(shopSummary.seller_total)}            accent="#15803d" bold />
              </div>
              {/* Summary cards row 3 — tips + final */}
              <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                <SummaryCard label="Tips Net Payout" value={money(shopSummary.tips_total)} accent="#7c3aed" bold
                  sub={shopSummary.ipay_pct > 0
                    ? `${shopSummary.tips_count} tips · gross ${money(shopSummary.tips_gross_total)} − iPay ${money(shopSummary.tips_ipay_total)}`
                    : `${shopSummary.tips_count} tips`} />
                <SummaryCard label="Final Payout"  value={money(shopSummary.final_payout)} accent="#15803d" sub="Product + Delivery + Tips" bold />
                <div style={{ flex: 4 }} />
              </div>

              {/* Table */}
              <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '0.6fr 1.4fr 0.9fr 1.4fr 0.9fr 0.8fr 0.8fr 1fr 0.8fr', padding: '10px 18px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
                  {['#','Buyer','Method','Delivery Zone / Source','Delivery Fee','Gross',`iPay ${iPctLabel}%`,'Payout','Date'].map(h => (
                    <div key={h} style={{ fontSize: 9, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
                  ))}
                </div>

                {shopLoading ? (
                  <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>Loading shop report…</div>
                ) : shopTxns.length === 0 ? (
                  <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>No paid orders for this shop in the selected period.</div>
                ) : (
                  shopTxns.map((t, i) => {
                    const b = shopBd(t.shop_subtotal, t.delivery_fee)
                    const hasZone = !!t.zone_name
                    return (
                      <div key={t.order_id || i}
                        style={{ display: 'grid', gridTemplateColumns: '0.6fr 1.4fr 0.9fr 1.4fr 0.9fr 0.8fr 0.8fr 1fr 0.8fr', padding: '11px 18px', borderBottom: i < shopTxns.length - 1 ? '1px solid #f3f3f0' : 'none', alignItems: 'center' }}>

                        {/* Order # */}
                        <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>#{t.order_id}</div>

                        {/* Buyer */}
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.buyer_name || 'Anonymous'}</div>
                          <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{t.buyer_phone || '—'}</div>
                        </div>

                        {/* Method */}
                        <div style={{ fontSize: 11, fontWeight: 600, fontFamily: "'JetBrains Mono',monospace", color: '#374151' }}>
                          {t.payment_method?.toUpperCase() || '—'}
                        </div>

                        {/* Zone / Fallback chip */}
                        <div>
                          {hasZone ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#f0fdf4', color: '#15803d', fontSize: 9, fontWeight: 700, padding: '3px 8px', borderRadius: 5, fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap' }}>
                              📍 {t.zone_name}{t.zone_city ? ` · ${t.zone_city}` : ''}
                            </span>
                          ) : t.delivery_fee > 0 ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#fff7ed', color: '#b45309', fontSize: 9, fontWeight: 700, padding: '3px 8px', borderRadius: 5, fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap' }}>
                              ⚡ Fallback Delivery Fee
                            </span>
                          ) : (
                            <span style={{ fontSize: 10, color: '#d1d5db', fontStyle: 'italic' }}>Free / None</span>
                          )}
                        </div>

                        {/* Delivery fee amount */}
                        <div>
                          <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", color: t.is_self_delivery ? '#1d4ed8' : '#b45309', fontWeight: 600 }}>{moneyN(b.delivery_fee)}</div>
                          {t.is_self_delivery && b.delivery_fee > 0 && (
                            <div style={{ fontSize: 8, fontWeight: 700, color: '#1d4ed8', fontFamily: "'JetBrains Mono',monospace", marginTop: 1 }}>SELLER DELIVERY</div>
                          )}
                        </div>

                        {/* Gross */}
                        <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>{moneyN(b.gross)}</div>

                        {/* iPay */}
                        <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", color: '#b91c1c' }}>{moneyN(b.ipay_fee)}</div>

                        {/* Payout */}
                        <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", fontWeight: 800, color: '#15803d' }}>{moneyN(b.seller_payout)}</div>

                        {/* Date */}
                        <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{fmt(t.paid_at)}</div>
                      </div>
                    )
                  })
                )}

                {/* Page total footer */}
                {!shopLoading && shopTxns.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: '0.6fr 1.4fr 0.9fr 1.4fr 0.9fr 0.8fr 0.8fr 1fr 0.8fr', padding: '12px 18px', borderTop: '2px solid #e9e9e4', background: '#f9f9f7', alignItems: 'center' }}>
                    <div />
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#111317' }}>Page Total ({shopTxns.length})</div>
                    <div /><div />
                    <div style={{ fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#b45309' }}>
                      {moneyN(shopTxns.reduce((s, t) => s + Number(t.delivery_fee || 0), 0))}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace" }}>
                      {moneyN(shopTxns.reduce((s, t) => s + Number(t.shop_subtotal || 0), 0))}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#b91c1c' }}>
                      {moneyN(shopTxns.reduce((s, t) => s + shopBd(t.shop_subtotal, t.delivery_fee).ipay_fee, 0))}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#15803d' }}>
                      {moneyN(shopTxns.reduce((s, t) => s + shopBd(t.shop_subtotal, t.delivery_fee).seller_payout, 0))}
                    </div>
                    <div />
                  </div>
                )}
              </div>

              {/* Shop pagination */}
              {shopTotalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8, marginBottom: 24 }}>
                  <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>
                    Page {shopPage} of {shopTotalPages} · {shopTotalCount.toLocaleString()} orders
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <PagBtn label="← Prev" disabled={shopPage === 1}             onClick={() => setShopPage(p => p - 1)} />
                    {Array.from({ length: Math.min(5, shopTotalPages) }, (_, i) => {
                      const p = Math.max(1, Math.min(shopPage - 2, shopTotalPages - 4)) + i
                      return <PagBtn key={p} label={p} active={p === shopPage} onClick={() => setShopPage(p)} />
                    })}
                    <PagBtn label="Next →" disabled={shopPage === shopTotalPages} onClick={() => setShopPage(p => p + 1)} />
                  </div>
                </div>
              )}

              {/* Tips section */}
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#111317', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                  💝 Tips Received
                  <span style={{ fontSize: 10, color: '#9a9a94', fontWeight: 400, fontFamily: "'JetBrains Mono',monospace" }}>
                    {shopSummary.tips_count} tips · net {money(shopSummary.tips_total)}
                    {shopSummary.ipay_pct > 0 && ` (gross ${money(shopSummary.tips_gross_total)} − iPay ${money(shopSummary.tips_ipay_total)})`}
                  </span>
                </div>
                <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
                  {shopSummary.ipay_pct > 0 && (
                    <div style={{ background: '#fffbeb', borderBottom: '1px solid #fde68a', padding: '8px 18px', fontSize: 11, color: '#92400e', fontFamily: "'JetBrains Mono',monospace" }}>
                      ℹ️ Gross (buyer paid) → iPay fee ({(shopSummary.ipay_pct*100).toFixed(0)}%) → platform tip commission deducted → net payout to seller
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '0.5fr 1.2fr 0.6fr 0.5fr 0.8fr 0.7fr 0.7fr 0.8fr 0.7fr', padding: '10px 18px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
                    {['#','Buyer','Method','Emoji','Gross Tip',`iPay ${shopSummary.ipay_pct>0?(shopSummary.ipay_pct*100).toFixed(0)+'%':''}`.trim(),'Platform','Net Payout','Date'].map(h => (
                      <div key={h} style={{ fontSize: 9, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
                    ))}
                  </div>
                  {shopTips.length === 0 ? (
                    <div style={{ padding: '28px 0', textAlign: 'center', color: '#9a9a94', fontSize: 12 }}>No tips for this shop in the selected period.</div>
                  ) : (
                    <>
                      {shopTips.map((t, i) => {
                        const gross   = Number(t.gross_amount || t.amount || 0)
                        const ipayFee = Number(t.ipay_fee     || 0)
                        const platFee = Number(t.platform_fee || 0)
                        const net     = Number(t.net_amount   || gross - ipayFee - platFee)
                        return (
                          <div key={t.tip_id || i} style={{ display: 'grid', gridTemplateColumns: '0.5fr 1.2fr 0.6fr 0.5fr 0.8fr 0.7fr 0.7fr 0.8fr 0.7fr', padding: '10px 18px', borderBottom: i < shopTips.length - 1 ? '1px solid #f3f3f0' : 'none', alignItems: 'center' }}>
                            <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>#{t.tip_id}</div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.buyer_name || 'Anonymous'}</div>
                              <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{t.buyer_phone || '—'}</div>
                            </div>
                            <div style={{ fontSize: 11, fontWeight: 600, fontFamily: "'JetBrains Mono',monospace", color: '#374151' }}>{(t.payment_method || '—').toUpperCase()}</div>
                            <div style={{ fontSize: 22, lineHeight: 1 }}>{t.emoji || '💰'}</div>
                            <div style={{ fontSize: 12, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#374151' }}>{moneyN(gross)}</div>
                            <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", color: '#b91c1c' }}>− {moneyN(ipayFee)}</div>
                            <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", color: '#7c3aed' }}>− {moneyN(platFee)}</div>
                            <div style={{ fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#e8440a' }}>{moneyN(net)}</div>
                            <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{fmt(t.created_at)}</div>
                          </div>
                        )
                      })}
                      <div style={{ display: 'grid', gridTemplateColumns: '0.5fr 1.2fr 0.6fr 0.5fr 0.8fr 0.7fr 0.7fr 0.8fr 0.7fr', padding: '11px 18px', borderTop: '2px solid #e9e9e4', background: '#f9f9f7', alignItems: 'center' }}>
                        <div /><div style={{ fontSize: 11, fontWeight: 800 }}>Total ({shopTips.length})</div>
                        <div /><div />
                        <div style={{ fontSize: 12, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#374151' }}>{moneyN(shopSummary.tips_gross_total)}</div>
                        <div style={{ fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#b91c1c' }}>− {moneyN(shopSummary.tips_ipay_total)}</div>
                        <div style={{ fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#7c3aed' }}>− {moneyN(shopSummary.tips_platform_total)}</div>
                        <div style={{ fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#e8440a' }}>{moneyN(shopSummary.tips_total)}</div>
                        <div />
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Final payout callout */}
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderLeft: '3px solid #15803d', borderRadius: 8, padding: '14px 18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace" }}>Final Payout to Seller</div>
                  <div style={{ fontSize: 26, fontWeight: 900, color: '#15803d', fontFamily: "'JetBrains Mono',monospace", letterSpacing: '-0.04em' }}>
                    {money(shopSummary.final_payout)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {[
                    { lbl: 'Product Payout', val: shopSummary.gross - shopSummary.ipay_total - shopSummary.platform_total, color: '#15803d' },
                    ...(shopSummary.seller_delivery_payout > 0 ? [{ lbl: `Self-Delivery Net`, val: shopSummary.seller_delivery_payout, color: '#1d4ed8' }] : []),
                    ...(shopSummary.tips_total > 0 ? [{ lbl: `Tips (${shopSummary.tips_count})`, val: shopSummary.tips_total, color: '#7c3aed' }] : []),
                  ].map(({ lbl, val, color }, idx, arr) => (
                    <div key={lbl} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ background: 'rgba(255,255,255,0.7)', borderRadius: 6, padding: '5px 10px', border: '1px solid rgba(0,0,0,0.06)' }}>
                        <div style={{ fontSize: 8, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 2 }}>{lbl}</div>
                        <div style={{ fontSize: 12, fontWeight: 800, color, fontFamily: "'JetBrains Mono',monospace" }}>{money(val)}</div>
                      </div>
                      {idx < arr.length - 1 && <span style={{ fontSize: 14, color: '#9ca3af', fontWeight: 700 }}>+</span>}
                    </div>
                  ))}
                </div>
                {shopTier?.tier_discount > 0 && (
                  <div style={{ marginTop: 8, fontSize: 11, color: '#15803d', fontFamily: "'JetBrains Mono',monospace" }}>
                    Tier: <TierBadge tier={shopTier.tier} small /> · Platform fee {(shopTier.platform_pct_effective * 100).toFixed(0)}% (base {(shopTier.platform_pct_base * 100).toFixed(0)}% − {(shopTier.tier_discount * 100).toFixed(0)}pp discount) · Saving: {money(Number(shopSummary.gross || 0) * shopTier.tier_discount)}
                  </div>
                )}
              </div>
            </>
          )}

          {!selectedShop && (
            <div style={{ background: '#f9f9f7', border: '1px solid #e9e9e4', borderRadius: 10, padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>
              Search and select a shop above to view its report.
            </div>
          )}
        </div>
      )}

      {/* Pagination — main transactions tab */}
      {refundTab === 'transactions' && totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>
            Page {page} of {totalPages} · {totalCount.toLocaleString()} transactions
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <PagBtn label="← Prev" disabled={page === 1}          onClick={() => setPage(p => p - 1)} />
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i
              return <PagBtn key={p} label={p} active={p === page} onClick={() => setPage(p)} />
            })}
            <PagBtn label="Next →" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} />
          </div>
        </div>
      )}

      {/* Drawer */}

      <TxnDrawer txn={selected} onClose={() => setSelected(null)} feeConfig={feeConfig} />
    </div>
  )
}

function PagBtn({ label, onClick, disabled, active }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ minWidth: 34, height: 34, borderRadius: 7, border: '1px solid #e9e9e4', background: active ? '#111317' : '#fff', color: active ? '#fff' : disabled ? '#9a9a94' : '#111317', fontSize: 12, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', padding: '0 10px', fontFamily: "'Plus Jakarta Sans',sans-serif", opacity: disabled ? 0.4 : 1 }}>
      {label}
    </button>
  )
}